import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AlertBox from '@/components/alert-box';
import CustomDocumentRequestForm from '@/components/document-builder/CustomDocumentRequestForm';
import { CUSTOM_TEMPLATE_PREFIX } from '@/components/request/TypeOfLeaveForm';
import useAppDataContext from '@/contexts/app-data/useAppDataContext';
import { cn } from '@/libs/utils';
import {
  BonusForm,
  HolidayLeaveForm,
  MaternityLeaveForm,
  NonPaidLeaveForm,
  OfficialTravelForm,
  PaidLeaveDeathForm,
  PaidLeaveGloryForm,
  PaidLeaveOtherForm,
  PaidLeaveWeddingForm,
  SuspensionForm,
  SickLeaveForm,
} from './request-form';

export default function EmployeeNewRequestForm({
  isEdited,
  formData,
  className,
  employeeId,
  selectedTypeOfLeave,
  handleRefetch,
  metadata,
}) {
  const { t } = useTranslation(['messages']);
  const { enums: enumsData } = useAppDataContext();
  const isEmployee = Boolean(enumsData?.isEmployee);

  const renderForm = useMemo(() => {
    const props = {
      formData,
      isEdited,
      employeeId,
      handleRefetch,
      typeOfLeave: selectedTypeOfLeave,
      isEmployee,
    };

    return {
      holidayLeave: <HolidayLeaveForm {...props} />,
      maternityLeave: <MaternityLeaveForm {...props} />,
      pregnancyLeave: <MaternityLeaveForm {...props} />,
      paidLeaveWedding: <PaidLeaveWeddingForm {...props} />,
      paidLeaveSlava: <PaidLeaveGloryForm {...props} />,
      paidLeaveDeath: <PaidLeaveDeathForm {...props} />,

      paidLeaveOther: <PaidLeaveOtherForm {...props} />,
      nonPaidLeave: <NonPaidLeaveForm {...props} />,

      bonus: <BonusForm {...props} />,

      suspension: <SuspensionForm {...props} />,

      officialTravel: <OfficialTravelForm {...props} />,
      workFromHome: <NonPaidLeaveForm {...props} />,

      sickLeave30_65: <SickLeaveForm {...props} />,
      sickLeave30Up_65: <SickLeaveForm {...props} />,
      sickLeave30_100: <SickLeaveForm {...props} />,
      sickLeave30Up_100: <SickLeaveForm {...props} />,
    };
  }, [
    formData,
    isEdited,
    employeeId,
    handleRefetch,
    selectedTypeOfLeave,
    isEmployee,
  ]);

  // Company is resolved from the employee record (needed for custom documents).
  const companyId = formData?.company?._id || formData?.company || null;

  // A custom document template picked from the leave-type dropdown is encoded as
  // `customTemplate:<id>` — render its inline generation form instead of a leave.
  const customTemplateId =
    typeof selectedTypeOfLeave === 'string' &&
    selectedTypeOfLeave.startsWith(CUSTOM_TEMPLATE_PREFIX)
      ? selectedTypeOfLeave.slice(CUSTOM_TEMPLATE_PREFIX.length)
      : null;

  const renderFormComponent = useCallback(() => {
    if (customTemplateId) {
      return (
        <CustomDocumentRequestForm
          companyId={companyId}
          employeeId={employeeId}
          templateId={customTemplateId}
        />
      );
    }
    return renderForm[selectedTypeOfLeave];
  }, [customTemplateId, companyId, employeeId, selectedTypeOfLeave, renderForm]);

  return (
    <div
      className={cn('grid gap-4 px-4 py-6', className, metadata?.formClassName)}
    >
      <AlertBox variant="info" className="relative mb-0">
        <p>
          {t(customTemplateId ? 'create_custom_document' : 'create_request', {
            ns: 'messages',
          })}
        </p>
      </AlertBox>

      {renderFormComponent()}
    </div>
  );
}
